import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

export function useScrollRestoration(isDataLoaded = true) {
  const location = useLocation();
  const history = useHistory();

  // Persistent states across renders
  const isRestoringScroll = useRef(false);
  const currentScrollPos = useRef(0);
  const pendingScrollRestore = useRef(null);

  // Disable Browser's Auto Scroll Restoration
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Restore scroll when data is loaded
  useEffect(() => {
    if (isDataLoaded && pendingScrollRestore.current !== null) {
      const scrollValue = pendingScrollRestore.current;

      // Wait for 2 animation frames (ensures DOM is fully painted)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollValue);
          currentScrollPos.current = window.scrollY;

          setTimeout(() => {
            isRestoringScroll.current = false;
            pendingScrollRestore.current = null;
          }, 150);
        });
      });
    }
  }, [isDataLoaded]);

  useEffect(() => {
    // Storage key
    const currentPath = `${location.pathname}${location.search}`;
    const storageKey = `scroll_${currentPath}`;

    if (history.action === 'POP') {
      const savedPosition = sessionStorage.getItem(storageKey);

      if (savedPosition) {
        isRestoringScroll.current = true;
        const scrollValue = parseInt(savedPosition, 10);

        if (isDataLoaded) {
          // Data is ready, restore immediately
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              window.scrollTo(0, scrollValue);
              currentScrollPos.current = scrollValue;
              setTimeout(() => {
                isRestoringScroll.current = false;
              }, 150);
            });
          });
        } else {
          // Wait for data to load
          pendingScrollRestore.current = scrollValue;
        }
      } else {
        window.scrollTo(0, 0);
        currentScrollPos.current = 0;
      }
    } else if (history.action === 'PUSH') {
      window.scrollTo(0, 0);
      currentScrollPos.current = 0;
      pendingScrollRestore.current = null;
    }

    // Scroll tracker
    let scrollTimeout;
    const handleScroll = () => {
      if (!isRestoringScroll.current) {
        const scrollY = window.scrollY;
        currentScrollPos.current = scrollY;

        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          sessionStorage.setItem(storageKey, scrollY.toString());
        }, 100);
      }
    };

    currentScrollPos.current = window.scrollY;
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);

      const finalScroll = currentScrollPos.current;
      sessionStorage.setItem(storageKey, finalScroll.toString());
    };
  }, [location.pathname, location.search, history.action, isDataLoaded]);

  return null;
}
